/**
 * PermissionCenterTab — Centrum uprawnień (F24 Kawałek 1).
 *
 * Ekran odpowiada na pytanie „co ten podmiot może w tej firmie", schodząc w dół drzewem obiektów:
 * Firma → kartoteka → klasa dokumentu → typ, oraz Firma → kartoteka → kategoria.
 *
 * Prawa dzielą się na CZTERY obszary (zakładki u góry), bo rządzą się różną logiką:
 *   • FIRMY — do których firm człowiek w ogóle ma wstęp. Warstwa nad resztą: bez dostępu do
 *     firmy żadna reguła poniżej nie ma znaczenia.
 *   • KARTOTEKI — drzewo obiektów (kartoteka → klasa → typ / kategoria), trójstan per akcja.
 *   • OGÓLNE i ADMINISTRACYJNE — reszta katalogu, nadawana kodem na poziomie firmy. Podział
 *     na te dwie zakładki trzyma `permission-areas.ts` (jedna lista kategorii).
 *
 * Trzy decyzje, które widać w układzie:
 *   • WYBÓR PODMIOTU po lewej (rola / zespół / osoba) — prawa nadaje się komuś, więc podmiot jest
 *     kontekstem całego ekranu, a nie kolejną kolumną.
 *   • DRZEWO W TABELI po prawej — wzorzec z planu kont: jedna kolumna nazwy z wcięciem i kolumny
 *     akcji. Tabela przestawna była rozważana i ODŁOŻONA (decyzja właściciela) — wróci osobno.
 *   • TRYB EDYCJI z „Zapisz" i „Anuluj" (decyzja właścicielki). Pierwsza wersja zapisywała
 *     każde kliknięcie od razu — szybkie, ale nie do wycofania: pomyłka na wierszu zbiorczym
 *     przestawiała kilkadziesiąt reguł i nie było jak wrócić. Teraz zmiany zbierają się lokalnie,
 *     tabela pokazuje je od razu (łącznie ze znacznikami wyniku), a do bazy idą jednym zapisem.
 *     Poza trybem edycji ekran jest do oglądania i nie da się nic ruszyć przypadkiem.
 *
 * Stan „Dziedziczone" liczymy TYM SAMYM kodem co serwer (`@saas/shared`), żeby duch w komórce
 * pokazywał prawdę, a nie własną interpretację frontu.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  evaluateOneSubject,
  evaluatePermission,
  explainPermission,
  OUT_OF_PROCESS_STAGE_KEY,
  type PermissionAction,
  type PermissionLayer,
  type PermissionObjectKind,
  type PermissionQuery,
  type PermissionRuleInput,
  type PermissionSubjectKind,
} from '@saas/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Copy,
  CopyPlus,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { FILTER_CONTROL_SIZE_CLASS } from '@/components/configurable-table/filter-row/control-classes';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { ConditionRulesPanel } from '@/components/permissions/ConditionRulesPanel';
import { CopyPermissionsModal } from '@/components/permissions/CopyPermissionsModal';
import { MenuAssignmentsPanel } from '@/components/permissions/MenuAssignmentsPanel';
import {
  areaOf,
  type CatalogDefinition,
  isOwnKartotekaNode,
} from '@/components/permissions/permission-areas';
import {
  PERMISSION_TABLE_CARD_CLASS,
  PERMISSION_TABLE_CLASS,
  PERMISSION_THEAD_CLASS,
} from '@/components/permissions/permission-table-classes';
import { PermissionCodesPanel } from '@/components/permissions/PermissionCodesPanel';
import {
  type PermissionCellChoice,
  type PermissionCellValue,
  PermissionSelect,
} from '@/components/permissions/PermissionSelect';
import {
  type PermissionSourceEntry,
  PermissionSourceModal,
} from '@/components/permissions/PermissionSourceModal';
import { RoleFromUserModal } from '@/components/permissions/RoleFromUserModal';
import { SubjectCompanyStrip } from '@/components/permissions/SubjectCompanyStrip';
import {
  SubjectFormModal,
  type SubjectFormTarget,
} from '@/components/permissions/SubjectFormModal';
import { SubjectMembersModal } from '@/components/permissions/SubjectMembersModal';
import { SidebarCollapseToggle } from '@/components/sidebar-collapse';
import { Spinner } from '@/components/Spinner';
import { Tabs } from '@/components/Tabs';
import { useToast } from '@/contexts/ToastContext';
import { usePermission } from '@/hooks/usePermission';
import { api } from '@/lib/api';
import {
  type PermissionRuleRow,
  type PermissionTreeNode,
  type RuleChange,
  TEAMS_KEY,
  useCompaniesForCenter,
  usePermissionDefinitions,
  usePermissionRules,
  usePermissionTree,
  useRolesForCenter,
  useTeams,
  useUpdatePermissionRules,
  useUsersForCenter,
} from '@/lib/use-permission-center';
import { useUserPermissions } from '@/lib/use-permissions';
import { cn } from '@/lib/utils';

type SubjectKind = 'role' | 'team' | 'user';

/** Zakładki obszarów uprawnień. Kolejność jak na ekranie: od najszerszego do najwęższego. */
const AREA_TABS = [
  'kartoteki',
  'ownRecords',
  'modules',
  'general',
  'admin',
  'conditions',
  'menu',
] as const;

type PermissionAreaTab = (typeof AREA_TABS)[number];

interface FlatRow {
  node: PermissionTreeNode;
  depth: number;
  /** Ścieżka od korzenia — potrzebna, żeby policzyć dziedziczenie dokładnie jak serwer. */
  path: PermissionTreeNode[];
  rowKey: string;
}

/** Spłaszczenie drzewa do wierszy tabeli, z pominięciem gałęzi zwiniętych. */
function flattenTree(
  nodes: PermissionTreeNode[],
  expanded: Set<string>,
  depth = 0,
  parents: PermissionTreeNode[] = [],
): FlatRow[] {
  const out: FlatRow[] = [];
  for (const node of nodes) {
    const rowKey = `${node.kind}:${node.key}:${node.id ?? ''}`;
    const path = [...parents, node];
    out.push({ node, depth, path, rowKey });
    if (node.children?.length && expanded.has(rowKey)) {
      out.push(...flattenTree(node.children, expanded, depth + 1, path));
    }
  }
  return out;
}

/**
 * Klucz komórki — pełna współrzędna, na której stoi reguła drzewiasta. Ten sam kształt dla
 * brudnopisu i dla reguł z bazy, żeby dało się je na siebie nałożyć bez zgadywania.
 */
function ruleCellKey(
  action: PermissionAction,
  objectKind: PermissionObjectKind,
  objectKey: string | null | undefined,
  objectId: string | null | undefined,
): string {
  return `${action}|${objectKind}|${objectKey ?? ''}|${objectId ?? ''}`;
}

/**
 * Klucz brudnopisu dla reguły KODOWEJ. Osobna przestrzeń od reguł drzewiastych: ten sam kod może
 * mieć regułę kodową na firmie i drzewiastą na typie, i są to dwa różne wiersze.
 */
function codeCellKey(code: string): string {
  return `code|${code}`;
}

/** Akcja wyjęta z kodu uprawnienia (`documents.contract.view` → `view`). */
function actionOfCode(code: string): PermissionAction {
  return code.slice(code.lastIndexOf('.') + 1) as PermissionAction;
}

/** Reguły z serwera w kształcie, jakiego oczekuje wspólny rdzeń rozstrzygania. */
function toEngineRules(rules: PermissionRuleRow[]): PermissionRuleInput[] {
  return rules.map((r) => ({
    subjectKind: r.subjectKind,
    subjectId: r.subjectId,
    permissionCode: r.permissionCode,
    action: r.action,
    objectKind: r.objectKind,
    objectKey: r.objectKey,
    objectId: r.objectId,
    effect: r.effect,
  }));
}

export function PermissionCenterTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const canManage = usePermission('crm_core.permissions.manage');

  const companies = useCompaniesForCenter();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const activeCompanyId = companyId ?? companies.data?.companies[0]?.id ?? null;

  const [subjectKind, setSubjectKind] = useState<SubjectKind>('role');
  const [subjectId, setSubjectId] = useState<string | null>(null);
  // Stabilna referencja — wchodzi do zależności rachunku reguł, więc nowy obiekt przy każdym
  // renderze przeliczałby całe drzewo bez powodu.
  const subject = useMemo(
    () => (subjectId ? { kind: subjectKind, id: subjectId } : null),
    [subjectKind, subjectId],
  );

  // Przy osobie: w których firmach ma dostęp i ile ról — do listy firm (bez dostępu = wyszarzona).
  // Ten sam odczyt co pasek „W tej firmie" i karta użytkownika (wspólna pamięć podręczna).
  const subjectAccess = useUserPermissions(subjectKind === 'user' ? subjectId : null);

  const roles = useRolesForCenter(activeCompanyId);
  const teams = useTeams(activeCompanyId);
  const users = useUsersForCenter();

  const tree = usePermissionTree(activeCompanyId);
  const definitions = usePermissionDefinitions();
  const rules = usePermissionRules(activeCompanyId, subject);
  const update = useUpdatePermissionRules(activeCompanyId, subject);

  /**
   * Panel wyboru podmiotu da się zwinąć — przy szerokim drzewie uprawnień zabierał jedną czwartą
   * ekranu, choć po wybraniu osoby nie jest już do niczego potrzebny.
   */
  const [panelOpen, setPanelOpen] = useState(true);

  /** Szukanie i stronicowanie listy podmiotów — przy kilkuset kontach lista bez tego nie działa. */
  const [subjectSearch, setSubjectSearch] = useState('');
  const [subjectPage, setSubjectPage] = useState(0);

  /** Obszar uprawnień pokazywany po prawej. */
  const [area, setArea] = useState<PermissionAreaTab>('kartoteki');

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  /**
   * Tryb edycji + zmiany jeszcze NIEZAPISANE, kluczowane współrzędną komórki (kod + obiekt).
   * Mapa, nie lista, bo kolejne kliknięcia w ten sam wiersz mają się nadpisywać, a nie kumulować.
   */
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState<Map<string, RuleChange>>(new Map());
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [subjectForm, setSubjectForm] = useState<SubjectFormTarget | null>(null);
  const [subjectToDelete, setSubjectToDelete] = useState<{
    kind: 'role' | 'team';
    id: string;
    name: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  /** Kopiowanie roli do innych firm i rola z uprawnień osoby (B12 ze szkolenia 14.10). */
  const [copyOpen, setCopyOpen] = useState(false);
  const [fromUserOpen, setFromUserOpen] = useState(false);

  /** Komorka, dla ktorej otwarto rozpisanie zrodel. */
  const [explained, setExplained] = useState<{ row: FlatRow; action: PermissionAction } | null>(
    null,
  );

  /**
   * Filtry wiersza nagłówka: nazwa obiektu plus stan per kolumna akcji. Pusty string = brak
   * filtra, spójnie z resztą list w apce.
   */
  const [nameFilter, setNameFilter] = useState('');
  const [stateFilters, setStateFilters] = useState<Record<PermissionAction, string>>({
    view: '',
    edit: '',
    delete: '',
  });
  const hasFilters = nameFilter.trim() !== '' || Object.values(stateFilters).some((v) => v !== '');

  /**
   * Do rachunku idą reguły WŁASNE podmiotu oraz — przy podmiocie OSOBA — reguły jej roli i
   * zespołów. Dzięki temu panel liczy dokładnie to samo, co serwer: rola pozwala, zespół
   * zabrania → nie wolno.
   */
  const engineRules = useMemo(() => {
    // Niezapisane zmiany muszą wejść do rachunku, inaczej znaczniki wyniku i rozpisanie źródeł
    // pokazywałyby stan sprzed edycji — czyli co innego, niż widać w kontrolkach obok.
    const own = (rules.data?.rules ?? []).filter((r) =>
      r.permissionCode != null
        ? !pending.has(codeCellKey(r.permissionCode))
        : r.action != null &&
          !pending.has(ruleCellKey(r.action, r.objectKind, r.objectKey, r.objectId)),
    );
    const added: PermissionRuleRow[] = [];
    if (subject) {
      for (const change of pending.values()) {
        if (change.effect === 'inherit') continue;
        const isCodeRule = change.objectKind === 'company';
        added.push({
          subjectKind: subject.kind,
          subjectId: subject.id,
          permissionCode: isCodeRule ? change.code : null,
          action: isCodeRule ? null : actionOfCode(change.code),
          objectKind: change.objectKind,
          objectKey: change.objectKey ?? null,
          objectId: change.objectId ?? null,
          effect: change.effect,
        });
      }
    }
    return toEngineRules([...own, ...added, ...(rules.data?.inheritedRules ?? [])]);
  }, [rules.data, pending, subject]);

  /** Mapa „podmiot → nazwa" do pokazania, SKĄD płynie uprawnienie. */
  const inheritedNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of rules.data?.inheritedSubjects ?? []) {
      map.set(`${s.kind}:${s.id}`, s.name);
    }
    return map;
  }, [rules.data]);
  /**
   * Filtr PRZYCINA drzewo, zachowując przodków trafień — inaczej dopasowany typ znikałby razem
   * z niedopasowaną klasą i nie dałoby się dojść, do czego właściwie należy. Przy aktywnym
   * filtrze rozwijamy to, co zostało, żeby trafienia nie chowały się pod zwiniętym rodzicem
   * (wzorzec z planu kont).
   */
  /**
   * Drzewo jest jedno, a zakładki dwie: KARTOTEKI pokazują kartoteki systemowe (w tym
   * kartoteki modułów zainstalowane przez ITE), KARTOTEKI WŁASNE — tylko te zbudowane przez
   * klienta. Pochodzenie niesie sam węzeł drzewa (`installedByIte`), więc podział nie zależy
   * od innych praw ani od tego, czy kartoteka jest włączona.
   */
  const areaNodes = useMemo(() => {
    const all = tree.data?.nodes ?? [];
    return area === 'ownRecords'
      ? all.filter(isOwnKartotekaNode)
      : all.filter((n) => !isOwnKartotekaNode(n));
  }, [tree.data, area]);

  const visibleNodes = useMemo(
    () => (hasFilters ? pruneNodes(areaNodes) : areaNodes),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pruneNodes czyta filtry i reguły
    [areaNodes, hasFilters, nameFilter, stateFilters, rules.data],
  );

  const effectiveExpanded = useMemo(
    () => (hasFilters ? new Set(visibleNodes.flatMap((n) => subtreeKeys(n))) : expanded),

    [hasFilters, visibleNodes, expanded],
  );

  const flat = useMemo(
    () => flattenTree(visibleNodes, effectiveExpanded),
    [visibleNodes, effectiveExpanded],
  );

  /**
   * Kody objęte drzewem kartotek — wyliczone z SAMEGO drzewa, żeby lista płaska nie pokazywała
   * po raz drugi tego, co nadaje się z hierarchią obok. Powielona stała rozjechałaby się przy
   * pierwszej nowej kartotece.
   */
  const treeCodes = useMemo(() => {
    const out = new Set<string>();
    const walk = (nodes: PermissionTreeNode[]): void => {
      for (const node of nodes) {
        if (node.codeNamespace) {
          for (const action of node.actions) out.add(`${node.codeNamespace}.${action}`);
        }
        walk(node.children ?? []);
      }
    };
    walk(tree.data?.nodes ?? []);
    return out;
  }, [tree.data]);

  /**
   * Kartoteki do wyboru w regule warunkowej — prosto z drzewa, żeby lista nie rozjechała się
   * z tym, co widać w zakładkach obok.
   */
  const conditionKartoteki = useMemo(
    () =>
      (tree.data?.nodes ?? []).map((node) => ({
        key: node.key,
        label: node.label ?? t(`tenant.permissions.kartoteka.${node.key}`),
      })),
    [tree.data, t],
  );

  /**
   * Klasy dokumentów — z drzewa, spod kartoteki Dokumenty.
   *
   * Reguła zawężona do klasy widzi POLA DODATKOWE tej klasy; bez zawężenia nie ma jak ich
   * zaproponować, bo każda klasa trzyma je w osobnej tabeli.
   */
  const conditionDocClasses = useMemo(() => {
    const documents = (tree.data?.nodes ?? []).find((node) => node.key === 'documents');
    return (documents?.children ?? []).map((node) => ({
      key: node.key,
      label: node.label ?? t(`tenant.permissions.docClass.${node.key}`),
    }));
  }, [tree.data, t]);

  /** Podmioty, którym da się przypiąć regułę — role i zespoły firmy oraz jej użytkownicy. */
  const conditionSubjects = useMemo(
    () => [
      ...(roles.data?.roleTemplates ?? []).map((r) => ({
        kind: 'role' as const,
        id: r.id,
        name: r.name,
      })),
      ...(teams.data?.teams ?? []).map((tm) => ({
        kind: 'team' as const,
        id: tm.id,
        name: tm.name,
      })),
      ...(users.data?.users ?? []).map((u) => ({
        kind: 'user' as const,
        id: u.id,
        name: u.displayName || u.email,
      })),
    ],
    [roles.data, teams.data, users.data],
  );

  /** Katalog bez kodów kartotek, rozbity na dwie zakładki. */
  const codesByArea = useMemo(() => {
    const out: Record<'ownRecords' | 'modules' | 'general' | 'admin', CatalogDefinition[]> = {
      ownRecords: [],
      modules: [],
      general: [],
      admin: [],
    };
    for (const def of definitions.data?.definitions ?? []) {
      if (treeCodes.has(def.code)) continue;
      out[areaOf(def)].push(def);
    }
    return out;
  }, [definitions.data, treeCodes]);

  /** Wybrana rola — do zmiany nazwy i usunięcia wprost z tego panelu. */
  const selectedRole = useMemo(
    () =>
      subjectKind === 'role' && subjectId
        ? ((roles.data?.roleTemplates ?? []).find((r) => r.id === subjectId) ?? null)
        : null,
    [subjectKind, subjectId, roles.data],
  );

  /** Wybrany zespół — potrzebny do okienka składu (nazwa w nagłówku). */
  const selectedTeam = useMemo(
    () =>
      subjectKind === 'team' && subjectId
        ? ((teams.data?.teams ?? []).find((tm) => tm.id === subjectId) ?? null)
        : null,
    [subjectKind, subjectId, teams.data],
  );

  const SUBJECTS_PER_PAGE = 12;

  const subjectOptionsAll = useMemo((): Array<{
    value: string;
    label: string;
    hint: string | null;
  }> => {
    if (subjectKind === 'role') {
      return (roles.data?.roleTemplates ?? []).map((r) => ({
        value: r.id,
        label: r.name,
        hint: null,
      }));
    }
    if (subjectKind === 'team') {
      return (teams.data?.teams ?? []).map((tm) => ({
        value: tm.id,
        label: tm.name,
        hint: tm.description,
      }));
    }
    return (users.data?.users ?? []).map((u) => ({
      value: u.id,
      label: u.displayName ?? u.email,
      hint: u.displayName ? u.email : null,
    }));
  }, [subjectKind, roles.data, teams.data, users.data]);

  const subjectMatches = useMemo(() => {
    const needle = subjectSearch.trim().toLowerCase();
    if (needle === '') return subjectOptionsAll;
    return subjectOptionsAll.filter(
      (opt) =>
        opt.label.toLowerCase().includes(needle) || (opt.hint ?? '').toLowerCase().includes(needle),
    );
  }, [subjectOptionsAll, subjectSearch]);

  const subjectPages = Math.max(1, Math.ceil(subjectMatches.length / SUBJECTS_PER_PAGE));
  // Strona mogła zostać po dłuższej liście (zmiana zakładki, wpisanie filtra) — przycinamy.
  const currentPage = Math.min(subjectPage, subjectPages - 1);
  const subjectOptions = subjectMatches.slice(
    currentPage * SUBJECTS_PER_PAGE,
    (currentPage + 1) * SUBJECTS_PER_PAGE,
  );

  /**
   * Akcje, które mają sens w TYM wierszu — własne albo zebrane z poddrzewa.
   *
   * Wiersz-kontener („Dokumenty") nie ma własnego kodu, ale musi dać się ustawić w dół, więc
   * pokazuje sumę akcji swoich dzieci. Bez tego jedyną drogą do ustawienia wszystkich klas
   * naraz byłoby sześć kliknięć.
   */
  function rowActions(node: PermissionTreeNode): PermissionAction[] {
    if (node.codeNamespace) return node.actions;
    const acc = new Set<PermissionAction>();
    const walk = (nodes: PermissionTreeNode[]): void => {
      for (const n of nodes) {
        for (const a of rowActions(n)) acc.add(a);
      }
    };
    walk(node.children ?? []);
    return (['view', 'edit', 'delete'] as PermissionAction[]).filter((a) => acc.has(a));
  }

  /**
   * Jawna reguła na tej współrzędnej — albo `inherit`, gdy jej nie ma.
   *
   * Najpierw patrzy w zmiany NIEZAPISANE: tabela ma pokazywać to, co użytkownik właśnie ustawił,
   * a nie to, co jeszcze leży w bazie.
   */
  function explicitRule(node: PermissionTreeNode, action: PermissionAction): PermissionCellChoice {
    // Wiersze-kontenery (kartoteka „Dokumenty", grupa procesu) nie mają własnej współrzędnej
    // reguły — ich stan wynika z tego, co leży pod nimi.
    if (node.codeNamespace === '') return 'inherit';
    const objectKind = node.kind as PermissionObjectKind;
    const draft = pending.get(ruleCellKey(action, objectKind, node.key, node.id ?? null));
    if (draft) return draft.effect;

    const found = (rules.data?.rules ?? []).find(
      (r) =>
        r.action === action &&
        r.objectKind === node.kind &&
        (r.objectKey ?? null) === node.key &&
        (r.objectId ?? null) === (node.id ?? null),
    );
    return found?.effect ?? 'inherit';
  }

  /** Węzły poddrzewa (bez samego węzła), które mogą nieść regułę tej akcji. */
  function descendantsWithRule(
    node: PermissionTreeNode,
    action: PermissionAction,
  ): PermissionTreeNode[] {
    const out: PermissionTreeNode[] = [];
    const walk = (nodes: PermissionTreeNode[]): void => {
      for (const n of nodes) {
        if (n.codeNamespace && n.actions.includes(action)) out.push(n);
        walk(n.children ?? []);
      }
    };
    walk(node.children ?? []);
    return out;
  }

  /** Ile jawnych ustawień siedzi POD tym wierszem — źródło stanu „Mieszane". */
  function overrideCount(node: PermissionTreeNode, action: PermissionAction): number {
    return descendantsWithRule(node, action).filter((n) => explicitRule(n, action) !== 'inherit')
      .length;
  }

  /**
   * Wartość komórki.
   *
   * Wiersz z dziećmi pokazuje **Mieszane**, gdy ustawienia pod nim NIE są jednolite. Bez tego
   * zwinięta gałąź wyglądałaby identycznie niezależnie od tego, czy wszystko jest dozwolone, czy
   * dwa typy mają zakaz — a to pierwsze pytanie, jakie zadaje się przy takim ekranie.
   *
   * Wiersz-kontener („Dokumenty") nie ma własnego kodu, więc jego stan liczymy z punktów
   * zaczepienia w poddrzewie.
   */
  function cellValue(row: FlatRow, action: PermissionAction): PermissionCellValue {
    const node = row.node;
    const own = node.codeNamespace ? explicitRule(node, action) : null;

    /** Najwyższe węzły z własnym kodem pod tym wierszem — one niosą stan gałęzi. */
    const anchors: PermissionTreeNode[] = [];
    const walkAnchors = (nodes: PermissionTreeNode[]): void => {
      for (const n of nodes) {
        if (n.codeNamespace && n.actions.includes(action)) anchors.push(n);
        else walkAnchors(n.children ?? []);
      }
    };
    walkAnchors(node.children ?? []);

    if (anchors.length === 0) return own ?? 'inherit';

    const below = anchors
      .map((a) => cellValue({ ...row, node: a }, action))
      // Dziecko BEZ własnego ustawienia bierze to, co ma rodzic — więc nie tworzy rozbieżności.
      // Bez tego wiersz z ustawieniem i dziećmi „zgodnie z rolą" wyglądał na niejednolity,
      // chociaż wszystko pod nim wychodzi tak samo.
      .map((v) => (v === 'inherit' && own !== null && own !== 'inherit' ? own : v));

    const all = own !== null ? [own, ...below] : below;
    const first = all[0]!;
    return all.every((v) => v === first) ? first : 'mixed';
  }

  /**
   * Pytanie o komórkę w kształcie, jaki rozumie wspólny rdzeń: punkt zaczepienia + ścieżka
   * obiektu od kartoteki w dół.
   *
   * Wiersz-kontener („Dokumenty") nie ma własnego kodu — prawa wiszą per klasa — więc pytamy o
   * jego pierwszy punkt zaczepienia. To jedno miejsce składania ścieżki dla wszystkich trzech
   * rzeczy, które o nią pytają (wynik z góry, jego źródło, rozpisanie w okienku); trzy osobne
   * wersje pokazywałyby trzy różne prawdy o tej samej komórce.
   */
  function cellQuery(
    row: FlatRow,
    action: PermissionAction,
  ): { anchor: PermissionTreeNode; query: PermissionQuery } | null {
    const node = row.node;
    const anchor = node.codeNamespace ? node : firstAnchor(node, action);
    if (!anchor) return null;
    const path = (node.codeNamespace ? row.path : [...row.path, anchor])
      // Wiersz-kontener nie jest obiektem reguły — wypada ze ścieżki.
      .filter((n) => n.codeNamespace !== '')
      .map((n) => ({ kind: n.kind as PermissionObjectKind, key: n.key, id: n.id ?? null }));
    return { anchor, query: { code: `${anchor.codeNamespace}.${action}`, action, path } };
  }

  /** Reguły bez tej jednej komórki — tak liczy się to, co wychodzi „z góry". */
  function rulesWithoutCell(
    anchor: PermissionTreeNode,
    action: PermissionAction,
    subjectOnly?: PermissionSubjectKind,
  ): PermissionRuleInput[] {
    return engineRules.filter(
      (r) =>
        !(
          (subjectOnly == null || r.subjectKind === subjectOnly) &&
          r.action === action &&
          r.objectKind === anchor.kind &&
          (r.objectKey ?? null) === anchor.key &&
          (r.objectId ?? null) === (anchor.id ?? null)
        ),
    );
  }

  /**
   * Co wyjdzie, gdy komórka zostanie „dziedziczona": liczymy ścieżkę BEZ jawnej reguły na tym
   * poziomie, dokładnie tak, jak zrobi to serwer.
   */
  function inheritedResult(row: FlatRow, action: PermissionAction): boolean {
    const cell = cellQuery(row, action);
    if (!cell) return false;
    return evaluatePermission(rulesWithoutCell(cell.anchor, action, subjectKind), cell.query)
      .allowed;
  }

  /**
   * Skąd płynie wynik przy braku własnej reguły: „rola Księgowa" albo „zespół Wdrożenia".
   * `null`, gdy rozstrzyga reguła tego samego podmiotu z wyższego poziomu obiektu — wtedy
   * „skąd" nie wnosi nic nowego.
   */
  function inheritedSource(row: FlatRow, action: PermissionAction): string | null {
    if (subjectKind !== 'user') return null;
    const cell = cellQuery(row, action);
    if (!cell) return null;

    const decision = evaluatePermission(
      rulesWithoutCell(cell.anchor, action, subjectKind),
      cell.query,
    );

    const by = decision.decidedBy;
    if (!by || by.subjectKind === 'user') return null;
    const name = inheritedNames.get(`${by.subjectKind}:${by.subjectId}`);
    if (!name) return null;
    return t(`tenant.permissions.center.source.${by.subjectKind}`, { name });
  }

  /**
   * Rozpisanie komórki na źródła — dane dla okienka „z czego to wynika".
   *
   * Przy podmiocie OSOBA liczymy KAŻDĄ jej rolę i KAŻDY zespół osobno (tym samym rdzeniem, co
   * serwer), bo przy sprzecznych ustawieniach to jedyny sposób, żeby zobaczyć, kto zabrania.
   * Przy roli i zespole źródłem jest ten sam podmiot na wyższym poziomie obiektu — wtedy
   * okienko pokazuje jeden wiersz i mówi, na którym poziomie wisi reguła.
   */
  function buildExplain(row: FlatRow, action: PermissionAction) {
    const cell = cellQuery(row, action);
    if (!cell) return null;

    // Zdejmujemy wyłącznie regułę TEGO podmiotu na tej współrzędnej — reguły ról i zespołów
    // zostają, bo właśnie o nie pytamy.
    const fromAbove = rulesWithoutCell(cell.anchor, action, subjectKind);

    const entries: PermissionSourceEntry[] = [];
    let layerResults: Partial<Record<PermissionLayer, 'allow' | 'deny' | null>> | undefined;
    let decidedLayer: PermissionLayer | null = null;
    if (subjectKind === 'user' && subjectId) {
      // Trzy warstwy (ADR-0103) — liczy je rdzeń silnika, ten sam co serwer. Podmioty bez żadnej
      // reguły (druga rola osoby, zespół bez ustawień) dopisujemy jawnie, żeby okienko pokazało je
      // jako „brak ustawień", a nie przemilczało.
      const explanation = explainPermission(fromAbove, cell.query, [
        { kind: 'user', id: subjectId },
        ...(rules.data?.inheritedSubjects ?? []).map((src) => ({ kind: src.kind, id: src.id })),
      ]);
      decidedLayer = explanation.decidedLayer;
      layerResults = {};
      for (const layer of explanation.layers) {
        layerResults[layer.layer] = layer.decision.decidedBy
          ? layer.decision.allowed
            ? 'allow'
            : 'deny'
          : null;
        for (const sub of layer.subjects) {
          entries.push({
            kind: sub.kind,
            id: sub.id,
            name:
              sub.kind === 'user'
                ? (subjectOptionsAll.find((o) => o.value === sub.id)?.label ?? '')
                : (inheritedNames.get(`${sub.kind}:${sub.id}`) ?? ''),
            effect: sub.decision.decidedBy ? (sub.decision.allowed ? 'allow' : 'deny') : null,
            objectKind: sub.decision.decidedBy?.objectKind ?? null,
          });
        }
      }
    } else if (subjectId) {
      const decision = evaluateOneSubject(fromAbove, subjectKind, subjectId, cell.query);
      entries.push({
        kind: subjectKind,
        id: subjectId,
        name: subjectOptionsAll.find((o) => o.value === subjectId)?.label ?? '',
        effect: decision.decidedBy ? (decision.allowed ? 'allow' : 'deny') : null,
        objectKind: decision.decidedBy?.objectKind ?? null,
      });
    }

    const final = evaluatePermission(fromAbove, cell.query);
    const by = final.decidedBy;
    const decidedBy = by
      ? {
          kind: by.subjectKind,
          name:
            inheritedNames.get(`${by.subjectKind}:${by.subjectId}`) ??
            subjectOptionsAll.find((o) => o.value === by.subjectId)?.label ??
            '',
        }
      : null;

    return {
      objectLabel: rowLabel(row.node),
      actionLabel: t(`tenant.permissions.center.col.${action}`),
      entries,
      result: final.allowed,
      decidedBy,
      layerResults,
      decidedLayer,
      // Własne ustawienie osoby przebija role i zespoły — wtedy rozbieżność między nimi
      // przestaje mieć znaczenie i okienko musi to powiedzieć wprost.
      ownOverride: subjectKind === 'user' && by?.subjectKind === 'user',
    };
  }

  /**
   * Etykieta stanu „bez własnej reguły".
   *
   * Dla OSOBY to naprawdę „zgodnie z rolą" — wynik płynie z roli albo z zespołu. Dla ROLI i
   * ZESPOŁU taka nazwa byłaby nieprawdą (to one SĄ źródłem), więc mówimy „z poziomu wyżej":
   * z kartoteki albo z klasy dokumentu ponad tym wierszem.
   */
  const inheritLabel =
    subjectKind === 'user'
      ? t('tenant.permissions.tri.inheritFromRole')
      : t('tenant.permissions.tri.inheritFromAbove');

  function firstAnchor(
    node: PermissionTreeNode,
    action: PermissionAction,
  ): PermissionTreeNode | null {
    for (const child of node.children ?? []) {
      if (child.codeNamespace && child.actions.includes(action)) return child;
      const deeper = firstAnchor(child, action);
      if (deeper) return deeper;
    }
    return null;
  }

  /**
   * Ustawia całe PODDRZEWO jednym ruchem — regułę dostaje KAŻDY poziom, aż do typów i kategorii.
   *
   * Pierwsza wersja stawiała regułę tylko najwyżej, gdzie miała sens, a niżej kasowała jawne
   * ustawienia — wynik był ten sam, ale tabela pokazywała wtedy „Zgodnie z rolą" na typach,
   * chociaż o dostępie przesądzało już ustawienie z góry. Na osobie czytało się to wprost
   * odwrotnie do prawdy: jak gdyby o typie nadal decydowała rola, która akurat go zabrania.
   *
   * Decyzja właścicielki: „zezwalaj" z wiersza zbiorczego ma zapisać zezwolenie wszędzie. Kosztem
   * jest kilkadziesiąt wierszy reguł zamiast kilku — cena za to, że ekran mówi dokładnie to, co
   * obowiązuje. Zdejmowanie („Zgodnie z rolą") działa tak samo: kasuje reguły z całej gałęzi.
   */
  function collectChanges(
    roots: PermissionTreeNode[],
    action: PermissionAction,
    next: PermissionCellChoice,
  ): RuleChange[] {
    const changes: RuleChange[] = [];

    const seen = new Set<string>();
    const push = (node: PermissionTreeNode, effect: PermissionCellChoice): void => {
      if (!node.codeNamespace || !node.actions.includes(action)) return;
      const key = `${node.kind}:${node.key}:${node.id ?? ''}`;
      if (seen.has(key)) return;
      seen.add(key);
      changes.push({
        code: `${node.codeNamespace}.${action}`,
        objectKind: node.kind as PermissionObjectKind,
        objectKey: node.key,
        objectId: node.id ?? null,
        effect,
      });
    };

    // Ustawienie z wiersza wyżej NIE schodzi na etapy procesu. Powód praktyczny: etapów są
    // dziesiątki na typ, więc jedno kliknięcie „zezwól" na kartotece wygenerowałoby setki
    // reguł i uderzyło w limit jednego zapisu. Powód merytoryczny: etap to wyjątek od
    // prawa do typu — nadaje się go świadomie, wiersz po wierszu, a nie hurtem.
    // Czy ustawienie ma schodzić na ETAPY. Z wiersza procesu — tak, bo etapów jednego obiegu
    // jest kilka i ustawienie ich naraz to normalny ruch. Z poziomów wyżej (kartoteka, klasa,
    // typ, cała firma) — nie: etapów są dziesiątki na typ, więc jedno kliknięcie generowałoby
    // setki reguł. Etap jest wyjątkiem od prawa do typu, nadawanym świadomie.
    const spreadToStages = roots.every(
      (r) => r.kind === 'process' || r.kind === 'process_step' || r.kind === 'process_any',
    );

    const walkAll = (nodes: PermissionTreeNode[]): void => {
      for (const n of nodes) {
        if (!spreadToStages && (n.kind === 'process_step' || n.kind === 'process_any')) continue;
        push(n, next);
        walkAll(n.children ?? []);
      }
    };
    walkAll(roots);

    return changes;
  }

  /** Dokłada zmiany do brudnopisu — do bazy idą dopiero przy „Zapisz". */
  function stageChanges(changes: RuleChange[]): void {
    if (changes.length === 0) return;
    setPending((prev) => {
      const next = new Map(prev);
      for (const change of changes) {
        next.set(
          change.objectKind === 'company'
            ? codeCellKey(change.code)
            : ruleCellKey(
                actionOfCode(change.code),
                change.objectKind,
                change.objectKey ?? null,
                change.objectId ?? null,
              ),
          change,
        );
      }
      return next;
    });
  }

  const pendingCount = pending.size;

  /**
   * Jeden zapis dla całego ekranu — reguły jednym żądaniem. Dostęp do firm i role osoby nie
   * zapisują się stąd: nadaje się je na karcie użytkownika (pasek firmy ma do niej link).
   */
  async function saveChanges(): Promise<void> {
    if (!activeCompanyId || !subject || pendingCount === 0) return;
    try {
      await update.mutateAsync([...pending.values()]);

      setPending(new Map());
      setEditing(false);
      toast.show({ variant: 'success', title: t('tenant.permissions.center.saved') });
    } catch {
      toast.show({ variant: 'error', title: t('tenant.permissions.center.saveError') });
    }
  }

  /**
   * Usunięcie roli albo zespołu. Obie trasy odmawiają, gdy podmiot jest w użyciu (przypisani
   * ludzie, nadane prawa) — komunikat serwera przekazujemy wprost, zamiast zgadywać powód.
   */
  async function deleteSubject(): Promise<void> {
    if (!subjectToDelete) return;
    setDeleting(true);
    try {
      const path =
        subjectToDelete.kind === 'role'
          ? `/role-templates/${subjectToDelete.id}`
          : `/teams/${subjectToDelete.id}`;
      await api.delete(path);
      await qc.invalidateQueries({ queryKey: ['permissions', 'center', 'roles'] });
      await qc.invalidateQueries({ queryKey: TEAMS_KEY });
      if (subjectId === subjectToDelete.id) setSubjectId(null);
      setSubjectToDelete(null);
      toast.show({
        variant: 'success',
        title: t('tenant.permissions.center.subjectAdmin.deleted'),
      });
    } catch (err) {
      const code = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.show({
        variant: 'error',
        title:
          code === 'ROLE_IN_USE' || code === 'TEAM_IN_USE'
            ? t('tenant.permissions.center.subjectAdmin.inUse')
            : code === 'SYSTEM_ROLE_IMMUTABLE'
              ? t('tenant.permissions.center.subjectAdmin.systemRole')
              : t('tenant.permissions.center.saveError'),
      });
    } finally {
      setDeleting(false);
    }
  }

  /** Wyjście z edycji bez zapisu — brudnopis znika, tabela wraca do stanu z bazy. */
  function discardChanges(): void {
    setPending(new Map());
    setEditing(false);
    setConfirmCancel(false);
  }

  /** Czy wiersz sam w sobie pasuje do filtrów (bez patrzenia na dzieci). */
  function matchesFilters(node: PermissionTreeNode): boolean {
    const needle = nameFilter.trim().toLowerCase();
    if (needle !== '' && !rowLabel(node).toLowerCase().includes(needle)) return false;

    for (const action of ['view', 'edit', 'delete'] as PermissionAction[]) {
      const wanted = stateFilters[action];
      if (wanted === '') continue;
      if (!rowActions(node).includes(action)) return false;
      if (cellValue({ node, depth: 0, path: [node], rowKey: '' }, action) !== wanted) return false;
    }
    return true;
  }

  function pruneNodes(nodes: PermissionTreeNode[]): PermissionTreeNode[] {
    const out: PermissionTreeNode[] = [];
    for (const node of nodes) {
      const children = pruneNodes(node.children ?? []);
      if (children.length > 0 || matchesFilters(node)) {
        out.push({ ...node, children });
      }
    }
    return out;
  }

  /** Stan komórki kodowej: reguła kodowa podmiotu na poziomie firmy albo jej brak. */
  function codeValue(code: string): PermissionCellChoice {
    const draft = pending.get(codeCellKey(code));
    if (draft) return draft.effect;
    const found = (rules.data?.rules ?? []).find(
      (r) => r.permissionCode === code && r.objectKind === 'company',
    );
    return found?.effect ?? 'inherit';
  }

  /** Co wyjdzie przy braku własnej reguły kodowej — bez ścieżki obiektu, sam kod. */
  function codeInherited(code: string): boolean {
    return evaluatePermission(rulesWithoutCode(code), { code, action: null, path: [] }).allowed;
  }

  function rulesWithoutCode(code: string): PermissionRuleInput[] {
    return engineRules.filter((r) => !(r.subjectKind === subjectKind && r.permissionCode === code));
  }

  /** Skąd płynie prawo do kodu — „z roli X" / „z zespołu Y" (tylko przy osobie). */
  function codeSource(code: string): string | null {
    if (subjectKind !== 'user') return null;
    const by = evaluatePermission(rulesWithoutCode(code), {
      code,
      action: null,
      path: [],
    }).decidedBy;
    if (!by || by.subjectKind === 'user') return null;
    const name = inheritedNames.get(`${by.subjectKind}:${by.subjectId}`);
    return name ? t(`tenant.permissions.center.source.${by.subjectKind}`, { name }) : null;
  }

  function setCode(code: string, next: PermissionCellChoice): void {
    setCodes([code], next);
  }

  /** Cała grupa (obszar albo moduł) jednym ruchem. */
  function setCodes(codes: string[], next: PermissionCellChoice): void {
    stageChanges(
      codes.map((code) => ({
        code,
        objectKind: 'company' as PermissionObjectKind,
        objectKey: null,
        objectId: null,
        effect: next,
      })),
    );
  }

  /** Klik na liściu — jedna komórka. Klik na wierszu z dziećmi — całe poddrzewo. */
  function setCell(row: FlatRow, action: PermissionAction, next: PermissionCellChoice): void {
    stageChanges(collectChanges([row.node], action, next));
  }

  /** Górny wiersz „Wszystko" — ten sam mechanizm zapuszczony na całe drzewo firmy. */
  function setAll(action: PermissionAction, next: PermissionCellChoice): void {
    stageChanges(collectChanges(tree.data?.nodes ?? [], action, next));
  }

  /** Stan wiersza „Wszystko": jednomyślność wszystkich kartotek albo „mieszane". */
  function allValue(action: PermissionAction): PermissionCellValue {
    const nodes = tree.data?.nodes ?? [];
    const values = nodes
      .filter((n) => rowActions(n).includes(action))
      .map((n) => cellValue({ node: n, depth: 0, path: [n], rowKey: '' }, action));
    if (values.length === 0) return 'inherit';
    const first = values[0]!;
    return values.every((v) => v === first) ? first : 'mixed';
  }

  /** Wszystkie klucze wierszy poddrzewa — do rozwinięcia/zwinięcia gałęzi za jednym razem. */
  function subtreeKeys(node: PermissionTreeNode): string[] {
    const out: string[] = [];
    const walk = (nodes: PermissionTreeNode[]): void => {
      for (const n of nodes) {
        if ((n.children?.length ?? 0) > 0) {
          out.push(`${n.kind}:${n.key}:${n.id ?? ''}`);
          walk(n.children ?? []);
        }
      }
    };
    walk([node]);
    return out;
  }

  /** Rozwija albo zwija CAŁĄ gałąź (wszystkie poziomy), nie tylko jeden krok. */
  function toggleBranch(node: PermissionTreeNode, open: boolean): void {
    const keys = subtreeKeys(node);
    setExpanded((prev) => {
      const nextSet = new Set(prev);
      for (const k of keys) {
        if (open) nextSet.add(k);
        else nextSet.delete(k);
      }
      return nextSet;
    });
  }

  /** Rozwija albo zwija całe drzewo firmy. */
  function toggleAllBranches(open: boolean): void {
    const keys = (tree.data?.nodes ?? []).flatMap((n) => subtreeKeys(n));
    setExpanded(open ? new Set(keys) : new Set());
  }

  function toggleExpand(rowKey: string): void {
    setExpanded((prev) => {
      const nextSet = new Set(prev);
      if (nextSet.has(rowKey)) nextSet.delete(rowKey);
      else nextSet.add(rowKey);
      return nextSet;
    });
  }

  /** Etykieta wiersza: dane mają własną nazwę, byty stałe tłumaczy panel. */
  function rowLabel(node: PermissionTreeNode): string {
    // Etap „poza procesem" jest bytem stałym, nie danymi tenanta — tłumaczy go panel, tak jak
    // kartoteki i klasy. Etap bez nazwy (węzeł nienazwany w kreatorze) też dostaje opis zamiast
    // surowego klucza, bo klucz nic człowiekowi nie mówi.
    // „Gdziekolwiek w obiegu" jest bytem stałym, nie danymi tenanta — tłumaczy go panel.
    if (node.kind === 'process_any') return t('tenant.permissions.center.stage.anyProcess');
    if (node.kind === 'process_step') {
      if (node.key === OUT_OF_PROCESS_STAGE_KEY) {
        return t('tenant.permissions.center.stage.outOfProcess');
      }
      return node.label || t('tenant.permissions.center.stage.unnamed');
    }
    // Wiersz procesu niesie jego nazwę z danych; bez nazwy zostaje opis zastępczy, nie uuid.
    if (node.kind === 'process') {
      return node.label || t('tenant.permissions.center.stage.unnamedProcess');
    }
    if (node.label) return node.label;
    if (node.kind === 'kartoteka') return t(`tenant.permissions.kartoteka.${node.key}`);
    if (node.kind === 'doc_class') return t(`tenant.permissions.docClass.${node.key}`);
    return node.key;
  }

  const ACTIONS: PermissionAction[] = ['view', 'edit', 'delete'];

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      {!panelOpen ? (
        // Przełącznik zwinięcia = kółko ze strzałką na prawej krawędzi karty — ten sam komponent
        // co panel nawigacji Ustawień i główny sidebar (spójny wzorzec „zwiń/rozwiń").
        <Card className="relative lg:flex-shrink-0">
          <SidebarCollapseToggle
            collapsed
            onToggle={() => setPanelOpen(true)}
            testId="permission-center-panel-toggle"
          />
          <div className="flex items-center gap-2 p-2 pt-8 lg:flex-col">
            {/* Po zwinięciu panelu nie wiadomo, czyje prawa są na ekranie — nazwa zostaje. */}
            <span className="truncate text-sm text-text-secondary lg:[writing-mode:vertical-rl]">
              {subjectOptionsAll.find((o) => o.value === subjectId)?.label ?? ''}
            </span>
          </div>
        </Card>
      ) : (
        <Card className="relative lg:w-72 lg:flex-shrink-0">
          <SidebarCollapseToggle
            collapsed={false}
            onToggle={() => setPanelOpen(false)}
            testId="permission-center-panel-toggle"
          />
          <div className="flex flex-col gap-3 p-4">
            {/* Firma jest pierwszym wyborem dla każdego podmiotu — rola należy do jednej firmy,
                a reguły zespołu i osoby też są per firma. */}
            <label className="mb-1 block text-sm font-medium text-text-secondary">
              {t('tenant.permissions.center.company')}
            </label>
            <Dropdown
              value={activeCompanyId ?? ''}
              // W trakcie edycji kontekst jest zamrożony — zmiana firmy albo podmiotu przy
              // niezapisanym brudnopisie znaczyłaby ciche wyrzucenie pracy.
              disabled={editing}
              onChange={(v) => {
                setCompanyId(v);
                // Rola i zespół należą do firmy — po zmianie firmy poprzedni wybór przestaje być
                // ważny.
                if (subjectKind !== 'user') setSubjectId(null);
              }}
              options={(companies.data?.companies ?? []).map((c) => {
                const name = c.shortName ?? c.legalName;
                // Przy osobie: firma bez jej dostępu jest wyszarzona (żadna reguła nic jej tam nie
                // da, dopóki nie dostanie wstępu), przy pozostałych — liczba ról osoby.
                if (subjectKind !== 'user' || !subjectId || !subjectAccess.data) {
                  return { value: c.id, label: name };
                }
                const acc = subjectAccess.data.companies.find((a) => a.companyId === c.id);
                return acc
                  ? {
                      value: c.id,
                      label: `${name} · ${t('tenant.permissions.center.companyTabs.roleCount', {
                        count: acc.roles?.length ?? 0,
                      })}`,
                    }
                  : {
                      value: c.id,
                      label: `${name} · ${t('tenant.permissions.center.companyTabs.noAccess')}`,
                      disabled: true,
                    };
              })}
            />

            {/* Rodzaj podmiotu = przełącznik segmentowy (Figma „Segmented control": szary tor,
                aktywna pozycja biała i uniesiona). Wygląd daje skórka `data-chip-bar` w index.css. */}
            <div
              className="flex gap-1"
              role="group"
              aria-label={t('tenant.permissions.center.subjectKindLabel')}
              data-chip-bar="groups"
              data-chip-bar-fill
            >
              {(['role', 'team', 'user'] as SubjectKind[]).map((kind) => (
                <Button
                  key={kind}
                  size="sm"
                  variant="ghost"
                  aria-pressed={subjectKind === kind}
                  disabled={editing}
                  className={cn(
                    'flex-1',
                    subjectKind === kind
                      ? 'font-medium text-text-primary'
                      : 'text-text-secondary hover:text-text-primary',
                  )}
                  onClick={() => {
                    setSubjectKind(kind);
                    setSubjectId(null);
                  }}
                >
                  {t(`tenant.permissions.center.subject.${kind}`)}
                </Button>
              ))}
            </div>

            <Input
              type="text"
              value={subjectSearch}
              onChange={(e) => {
                setSubjectSearch(e.target.value);
                setSubjectPage(0);
              }}
              placeholder={t('tenant.permissions.center.searchSubject')}
              aria-label={t('tenant.permissions.center.searchSubject')}
              prefixIcon={<Search className="h-3.5 w-3.5" />}
              className={FILTER_CONTROL_SIZE_CLASS}
            />

            {/*
            Wiersz na osobę, nie zawijana etykieta na środku: nazwa do lewej, adres pod spodem
            drobnym drukiem. Przy kontach o podobnych nazwiskach adres jest jedynym rozróżnieniem.
          */}
            <ul className="flex flex-col divide-y divide-border-primary/50 overflow-hidden rounded-lg border border-border-primary">
              {subjectOptions.map((opt) => (
                <li key={opt.value}>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={editing && subjectId !== opt.value}
                    onClick={() => setSubjectId(opt.value)}
                    className={cn(
                      'h-auto w-full justify-start rounded-none px-3.5 py-2.5 text-left font-normal',
                      subjectId === opt.value
                        ? 'bg-accent-50 font-medium text-text-primary dark:bg-accent-950'
                        : 'hover:bg-bg-secondary',
                    )}
                  >
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="truncate leading-tight">{opt.label}</span>
                      {opt.hint && (
                        <span className="truncate text-xs font-normal leading-tight text-text-muted">
                          {opt.hint}
                        </span>
                      )}
                    </span>
                  </Button>
                </li>
              ))}
              {subjectOptions.length === 0 && (
                <li className="px-3.5 py-3 text-sm text-text-muted">
                  {subjectSearch.trim() === ''
                    ? t('tenant.permissions.center.noSubjects')
                    : t('tenant.permissions.center.noSubjectMatches')}
                </li>
              )}
            </ul>

            {subjectPages > 1 && (
              <div className="flex items-center justify-between gap-2">
                <IconButton
                  size="sm"
                  disabled={currentPage === 0}
                  aria-label={t('common.previous')}
                  onClick={() => setSubjectPage(currentPage - 1)}
                >
                  <ChevronLeft size={14} aria-hidden="true" />
                </IconButton>
                <span className="text-xs text-text-muted">
                  {t('tenant.permissions.center.pageOf', {
                    page: currentPage + 1,
                    pages: subjectPages,
                    total: subjectMatches.length,
                  })}
                </span>
                <IconButton
                  size="sm"
                  disabled={currentPage >= subjectPages - 1}
                  aria-label={t('common.next')}
                  onClick={() => setSubjectPage(currentPage + 1)}
                >
                  <ChevronRight size={14} aria-hidden="true" />
                </IconButton>
              </div>
            )}

            {/*
            Rolę zakłada się TU, a nie na osobnym ekranie: nowa rola jest pusta, więc jedyne, co
            się z nią robi po utworzeniu, to nadanie uprawnień w drzewie obok. Odsyłanie po to
            do listy ról i z powrotem byłoby drogą naokoło.
          */}
            {/*
            Do czego osoba należy. Bez tego trzeba było wchodzić w komórkę i otwierać rozpisanie
            źródeł, żeby w ogóle się dowiedzieć, że ktoś ma rolę albo siedzi w zespole — a to
            pierwsze pytanie przy oglądaniu uprawnień człowieka.
          */}
            {/*
            Zakładanie, nazywanie i kasowanie podmiotu siedzi TU, przy liście, a nie na osobnych
            ekranach „Szablony ról" i „Zespoły". Z rolą i tak nic się nie robi poza nadaniem jej
            praw w drzewie obok, więc rozbicie tego na dwa miejsca zmuszało do skakania.
          */}
            {subjectKind !== 'user' && (
              <div className="flex flex-col gap-1 border-t border-border-primary pt-3">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!canManage.allowed || editing || !activeCompanyId}
                  title={canManage.allowed ? undefined : t('tenant.permissions.center.noManage')}
                  onClick={() =>
                    setSubjectForm({ kind: subjectKind, id: null, name: '', description: null })
                  }
                >
                  <Plus size={14} aria-hidden="true" />
                  {t(`tenant.permissions.center.subjectAdmin.new.${subjectKind}`)}
                </Button>

                {(selectedTeam ?? selectedRole) && (
                  <Button variant="ghost" size="sm" onClick={() => setMembersOpen(true)}>
                    <Users size={14} aria-hidden="true" />
                    {t('tenant.teams.members.open')}
                  </Button>
                )}

                {(selectedRole ?? selectedTeam) && canManage.allowed && !editing && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setSubjectForm(
                          selectedRole
                            ? {
                                kind: 'role',
                                id: selectedRole.id,
                                name: selectedRole.name,
                                description: null,
                              }
                            : {
                                kind: 'team',
                                id: selectedTeam!.id,
                                name: selectedTeam!.name,
                                description: selectedTeam!.description,
                              },
                        )
                      }
                    >
                      <Pencil size={14} aria-hidden="true" />
                      {t('tenant.permissions.center.subjectAdmin.renameAction')}
                    </Button>
                    {/* Uprawnienia są per firma — bez kopiowania te same ustawienia roli albo
                        zespołu wdrożeniowiec układał w każdej firmie od nowa. */}
                    <Button variant="ghost" size="sm" onClick={() => setCopyOpen(true)}>
                      <Copy size={14} aria-hidden="true" />
                      {t('tenant.permissions.center.copyPermissions.action')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 dark:text-red-400"
                      onClick={() =>
                        setSubjectToDelete(
                          selectedRole
                            ? { kind: 'role', id: selectedRole.id, name: selectedRole.name }
                            : { kind: 'team', id: selectedTeam!.id, name: selectedTeam!.name },
                        )
                      }
                    >
                      <Trash2 size={14} aria-hidden="true" />
                      {t('tenant.permissions.center.subjectAdmin.deleteAction')}
                    </Button>
                  </>
                )}
              </div>
            )}

            {/*
              Przy osobie: jej indywidualne ustawienia da się przenieść do innych firm tenanta, a jej
              REALNE uprawnienia w tej firmie (role + zespoły + jej ustawienia) zebrać w nową rolę
              i tę rolę nadać innym. Kopiowania praw osoby na inną osobę nie ma — pracujemy na rolach.
            */}
            {subjectKind === 'user' && subjectId && canManage.allowed && !editing && (
              <div className="flex flex-col gap-1 border-t border-border-primary pt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!activeCompanyId}
                  onClick={() => setCopyOpen(true)}
                >
                  <Copy size={14} aria-hidden="true" />
                  {t('tenant.permissions.center.copyPermissions.action')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!activeCompanyId}
                  onClick={() => setFromUserOpen(true)}
                >
                  <CopyPlus size={14} aria-hidden="true" />
                  {t('tenant.permissions.center.fromUser.action')}
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      <Card className="min-w-0 flex-1">
        {!subject ? (
          <p className="p-6 text-sm text-text-muted">
            {t('tenant.permissions.center.pickSubject')}
          </p>
        ) : tree.isLoading || rules.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/*
              Tryb edycji obejmuje WSZYSTKIE zakładki, więc jego przyciski stoją nad nimi. Wcześniej
              siedziały pod zakładkami i czytały się jak „edytuj tę jedną listę", choć brudnopis od
              początku był wspólny: można ustawić prawa w kartotekach, dołożyć kod administracyjny
              i zapisać to jednym ruchem.
            */}
            <div className="flex flex-wrap items-center justify-end gap-2 border-b border-border-primary px-5 py-3">
              {/*
                CZYJE prawa i W KTÓREJ firmie. Panel wyboru da się zwinąć, a i bez tego przy
                przewiniętej tabeli łatwo zapomnieć, komu się właśnie coś nadaje — pomyłka oznacza
                nadanie praw nie tej osobie albo nie w tej firmie.

                Nazwa firmy znika na zakładce „Firmy": tamta tabela obejmuje WSZYSTKIE firmy
                tenanta, więc wskazywanie jednej byłoby nieprawdą.
              */}
              <span className="mr-auto min-w-0 truncate text-sm text-text-muted">
                {t('tenant.permissions.center.company')}:{' '}
                <span className="text-text-secondary">
                  {(companies.data?.companies ?? []).find((c) => c.id === activeCompanyId)
                    ?.shortName ??
                    (companies.data?.companies ?? []).find((c) => c.id === activeCompanyId)
                      ?.legalName ??
                    ''}
                </span>
                {' · '}
                {t(`tenant.permissions.center.subject.${subjectKind}`)}:{' '}
                <span className="text-text-secondary">
                  {subjectOptionsAll.find((o) => o.value === subjectId)?.label ?? ''}
                </span>
                {editing && pendingCount > 0 && (
                  <span className="text-text-secondary">
                    {' · '}
                    {t('tenant.permissions.center.pendingCount', { count: pendingCount })}
                  </span>
                )}
              </span>
              {/*
                Zakładka reguł warunkowych nie ma czego zapisywać tym przyciskiem — każda reguła
                zapisuje się w swoim oknie. Pokazywanie tu „Edytuj / Zapisz" sugerowałoby, że bez
                nich nic się nie da, a po dodaniu reguły — że trzeba jeszcze coś zatwierdzić.

                ALE tryb edycji zaczęty na innej zakładce trwa dalej (i blokuje zmianę osoby) — wtedy
                „Anuluj / Zapisz" MUSZĄ być widoczne także tu, inaczej nie da się z niego wyjść.
              */}
              {(area === 'conditions' || area === 'menu') && !editing ? null : editing ? (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={update.isPending}
                    onClick={() => (pendingCount > 0 ? setConfirmCancel(true) : discardChanges())}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    size="sm"
                    loading={update.isPending}
                    disabled={pendingCount === 0}
                    onClick={() => void saveChanges()}
                  >
                    {t('common.save')}
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  disabled={!canManage.allowed}
                  title={canManage.allowed ? undefined : t('tenant.permissions.center.noManage')}
                  onClick={() => setEditing(true)}
                >
                  {t('common.edit')}
                </Button>
              )}
            </div>

            {/*
              Zakładki obszarów. „Firmy" dotyczy wyłącznie osoby — rola i zespół należą do jednej
              firmy z definicji, więc nie ma tam czego zaznaczać.
            */}
            {/* Zakładki obszarów = zakładki z podkreśleniem (Figma 08.4 „Tabs · Underline" —
                sekcje na stronie), jak w pozostałych ekranach Ustawień. */}
            {subjectKind === 'user' && subjectId && (
              <SubjectCompanyStrip
                userId={subjectId}
                companies={companies.data?.companies ?? []}
                activeCompanyId={activeCompanyId}
                onSelect={setCompanyId}
                teams={(rules.data?.inheritedSubjects ?? []).filter((src) => src.kind === 'team')}
                disabled={editing}
              />
            )}
            <Tabs
              value={area}
              onChange={(tab) => setArea(tab as typeof area)}
              idPrefix="permission-center-area"
              className="px-5 pt-2"
              items={AREA_TABS.map((tab) => ({
                value: tab,
                label: t(`tenant.permissions.center.area.${tab}`),
                content: null,
              }))}
            />

            {/* Rozwinięcie/zwinięcie całego drzewa — przy siedmiu klasach dokumentów i kilkunastu
                typach chodzenie po jednym poziomie jest nie do zniesienia. */}
            {/* Treść zakładki z oddechem karty (Figma: padding karty 20 px). */}
            <div className="px-5 pb-5 pt-3">
              {(area === 'kartoteki' || area === 'ownRecords') && (
                <div className="flex flex-wrap items-center justify-end gap-2 pb-3">
                  <Button variant="ghost" size="sm" onClick={() => toggleAllBranches(true)}>
                    {t('tenant.permissions.center.expandAll')}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleAllBranches(false)}>
                    {t('tenant.permissions.center.collapseAll')}
                  </Button>
                </div>
              )}
              {area === 'menu' && subjectId ? (
                <MenuAssignmentsPanel
                  subject={{ kind: subjectKind, id: subjectId }}
                  companyId={activeCompanyId}
                  canManage={canManage.allowed}
                />
              ) : area === 'conditions' ? (
                <ConditionRulesPanel
                  companyId={activeCompanyId}
                  kartoteki={conditionKartoteki}
                  docClasses={conditionDocClasses}
                  subjects={conditionSubjects}
                  preselectedSubject={subjectId ? { kind: subjectKind, id: subjectId } : null}
                  canManage={canManage.allowed}
                />
              ) : area === 'modules' || area === 'general' || area === 'admin' ? (
                <PermissionCodesPanel
                  definitions={codesByArea[area]}
                  valueOf={codeValue}
                  inheritedOf={codeInherited}
                  sourceOf={codeSource}
                  onChange={setCode}
                  onChangeGroup={setCodes}
                  inheritLabel={inheritLabel}
                  editing={editing}
                  emptyHint={t('tenant.permissions.center.codes.empty')}
                />
              ) : (
                <div className={PERMISSION_TABLE_CARD_CLASS}>
                  <table className={PERMISSION_TABLE_CLASS} data-skin="antd">
                    <thead className={PERMISSION_THEAD_CLASS}>
                      <tr className="text-left text-text-secondary">
                        <th className="px-3 py-2 font-medium">
                          {t('tenant.permissions.center.col.object')}
                        </th>
                        {ACTIONS.map((a) => (
                          <th key={a} className="w-28 px-3 py-2 font-medium">
                            {t(`tenant.permissions.center.col.${a}`)}
                          </th>
                        ))}
                      </tr>
                      {/* Wiersz filtrów — te same miary kontrolek co w pozostałych listach
                    (FILTER_CONTROL_SIZE_CLASS), żeby Centrum nie wyglądało inaczej. */}
                      <tr>
                        <th className="px-2 py-2 align-top">
                          <Input
                            type="text"
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                            aria-label={t('tenant.permissions.center.col.object')}
                            prefixIcon={<Search className="h-3.5 w-3.5" />}
                            className={FILTER_CONTROL_SIZE_CLASS}
                            data-testid="permission-center-filter-object"
                          />
                        </th>
                        {ACTIONS.map((action) => (
                          <th key={action} className="px-2 py-2 align-top">
                            <Dropdown
                              value={stateFilters[action]}
                              onChange={(val) =>
                                setStateFilters((prev) => ({ ...prev, [action]: val }))
                              }
                              options={[
                                { value: '', label: t('ui.configurableTable.selectFilter.all') },
                                { value: 'allow', label: t('tenant.permissions.tri.allow') },
                                { value: 'deny', label: t('tenant.permissions.tri.deny') },
                                { value: 'inherit', label: inheritLabel },
                              ]}
                              aria-label={t(`tenant.permissions.center.col.${action}`)}
                              className={FILTER_CONTROL_SIZE_CLASS}
                            />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {/*
                  „Wszystko" — jedno kliknięcie ustawia całą firmę. Reguła ląduje NAJWYŻEJ, gdzie
                  ma sens (kartoteka albo klasa dokumentu), a wszystko poniżej wraca do
                  dziedziczenia — inaczej tabela zapełniłaby się setkami reguł na każdym typie.
                */}
                      {/* Przy aktywnym filtrze wiersz „Wszystko" byłby mylący: ustawiałby CAŁĄ firmę,
                    a widać tylko trafienia. Chowamy go, zamiast pozwolić na taką pomyłkę. */}
                      {!hasFilters && (
                        <tr className="border-b border-border-primary bg-bg-tertiary/40">
                          <td className="px-3 py-2 font-medium text-text-primary">
                            {t('tenant.permissions.center.all')}
                          </td>
                          {ACTIONS.map((action) => (
                            <td key={action} className="px-3 py-2">
                              <PermissionSelect
                                value={allValue(action)}
                                // Nad „wszystkim" nie ma JEDNEGO wyniku do pokazania: każda kartoteka
                                // może dziedziczyć co innego. Znacznik zostaje pusty, zamiast zgadywać.
                                inheritedResult={null}
                                inheritLabel={inheritLabel}
                                disabled={!editing || update.isPending}
                                onChange={(next) => setAll(action, next)}
                              />
                            </td>
                          ))}
                        </tr>
                      )}
                      {flat.map((row) => {
                        const hasChildren = (row.node.children?.length ?? 0) > 0;
                        const hasGrandchildren = (row.node.children ?? []).some(
                          (c) => (c.children?.length ?? 0) > 0,
                        );
                        return (
                          <tr
                            key={row.rowKey}
                            className="border-b border-border-primary/25 even:bg-bg-tertiary/20"
                          >
                            <td className="px-3 py-1">
                              <div
                                className="flex items-center gap-1"
                                style={{ paddingLeft: row.depth * 20 }}
                              >
                                {hasChildren ? (
                                  <IconButton
                                    size="sm"
                                    onClick={() => toggleExpand(row.rowKey)}
                                    aria-label={t('tenant.permissions.center.toggle')}
                                  >
                                    {expanded.has(row.rowKey) ? (
                                      <ChevronDown size={14} aria-hidden="true" />
                                    ) : (
                                      <ChevronRight size={14} aria-hidden="true" />
                                    )}
                                  </IconButton>
                                ) : (
                                  <span className="w-[22px]" />
                                )}
                                {/*
                            Drugi przycisk rozwija/zwija CAŁĄ gałąź, nie jeden poziom. Przy
                            dokumentach jeden krok pokazuje tylko siedem klas — żeby dojść do typów,
                            trzeba było klikać siedem razy.
                          */}
                                {hasGrandchildren && (
                                  <IconButton
                                    size="sm"
                                    onClick={() =>
                                      toggleBranch(row.node, !expanded.has(row.rowKey))
                                    }
                                    aria-label={
                                      expanded.has(row.rowKey)
                                        ? t('tenant.permissions.center.collapseBranch')
                                        : t('tenant.permissions.center.expandBranch')
                                    }
                                    title={
                                      expanded.has(row.rowKey)
                                        ? t('tenant.permissions.center.collapseBranch')
                                        : t('tenant.permissions.center.expandBranch')
                                    }
                                  >
                                    {expanded.has(row.rowKey) ? (
                                      <ChevronsDownUp size={14} aria-hidden="true" />
                                    ) : (
                                      <ChevronsUpDown size={14} aria-hidden="true" />
                                    )}
                                  </IconButton>
                                )}
                                <span>{rowLabel(row.node)}</span>
                                {row.node.origin === 'global' && (
                                  <span className="text-xs text-text-muted">
                                    {t('tenant.permissions.center.globalType')}
                                  </span>
                                )}
                                {/*
                              Węzeł, na którym dokument nigdy nie stoi (start, rozgałęzienie;
                              etap końcowy NIE — sprawa zakończona stoi na nim). Pokazujemy go,
                              żeby drzewo odwzorowywało schemat obiegu, ale mówimy wprost, że
                              prawo tu nadane nie ma czego rozstrzygać — inaczej byłby cichą
                              pułapką: klik bez skutku.
                            */}
                                {row.node.stageHoldsRecord === false && (
                                  <span
                                    className="text-xs text-text-muted"
                                    title={t('tenant.permissions.center.stage.passThroughHint')}
                                  >
                                    {t('tenant.permissions.center.stage.passThrough')}
                                  </span>
                                )}
                              </div>
                            </td>
                            {ACTIONS.map((action) => (
                              <td key={action} className="px-3 py-1">
                                {rowActions(row.node).includes(action) ? (
                                  <PermissionSelect
                                    value={cellValue(row, action)}
                                    inheritedResult={inheritedResult(row, action)}
                                    inheritedFrom={inheritedSource(row, action)}
                                    inheritLabel={inheritLabel}
                                    onExplain={() => setExplained({ row, action })}
                                    overrideCount={overrideCount(row.node, action)}
                                    disabled={!editing || update.isPending}
                                    onChange={(next) => setCell(row, action, next)}
                                  />
                                ) : (
                                  <span className="text-text-muted">—</span>
                                )}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      <SubjectMembersModal
        open={membersOpen}
        subject={
          selectedTeam
            ? { kind: 'team', id: selectedTeam.id, name: selectedTeam.name }
            : selectedRole
              ? { kind: 'role', id: selectedRole.id, name: selectedRole.name }
              : null
        }
        companyId={activeCompanyId}
        canManage={canManage.allowed}
        onClose={() => setMembersOpen(false)}
      />

      <CopyPermissionsModal
        open={copyOpen}
        subject={
          selectedRole
            ? { kind: 'role', id: selectedRole.id, name: selectedRole.name }
            : selectedTeam
              ? { kind: 'team', id: selectedTeam.id, name: selectedTeam.name }
              : subjectKind === 'user' && subjectId
                ? {
                    kind: 'user',
                    id: subjectId,
                    name: subjectOptionsAll.find((o) => o.value === subjectId)?.label ?? '',
                  }
                : null
        }
        sourceCompanyId={activeCompanyId}
        companies={companies.data?.companies ?? []}
        onClose={() => setCopyOpen(false)}
      />

      <RoleFromUserModal
        open={fromUserOpen}
        companyId={activeCompanyId}
        user={
          subjectKind === 'user' && subjectId
            ? {
                id: subjectId,
                label: subjectOptionsAll.find((o) => o.value === subjectId)?.label ?? '',
              }
            : null
        }
        onClose={() => setFromUserOpen(false)}
        onCreated={(roleId) => {
          setSubjectKind('role');
          setSubjectId(roleId);
        }}
      />

      <SubjectFormModal
        open={subjectForm !== null}
        target={subjectForm}
        companyId={activeCompanyId}
        onClose={() => setSubjectForm(null)}
        onCreated={(kind, id) => {
          setSubjectKind(kind);
          setSubjectId(id);
        }}
      />

      <ConfirmDialog
        open={subjectToDelete !== null}
        variant="danger"
        title={t('tenant.permissions.center.subjectAdmin.deleteTitle')}
        message={t('tenant.permissions.center.subjectAdmin.deleteMessage', {
          name: subjectToDelete?.name ?? '',
        })}
        confirming={deleting}
        onConfirm={() => void deleteSubject()}
        onClose={() => setSubjectToDelete(null)}
      />

      <ConfirmDialog
        open={confirmCancel}
        title={t('tenant.permissions.center.discardTitle')}
        message={t('tenant.permissions.center.discardMessage', { count: pending.size })}
        confirmLabel={t('tenant.permissions.center.discardConfirm')}
        onConfirm={discardChanges}
        onClose={() => setConfirmCancel(false)}
      />

      {explained &&
        (() => {
          const data = buildExplain(explained.row, explained.action);
          if (!data) return null;
          return (
            <PermissionSourceModal
              open
              onClose={() => setExplained(null)}
              objectLabel={data.objectLabel}
              actionLabel={data.actionLabel}
              entries={data.entries}
              result={data.result}
              decidedBy={data.decidedBy}
              ownOverride={data.ownOverride}
              layerResults={data.layerResults}
              decidedLayer={data.decidedLayer}
              // Przejście ma sens tylko z osoby: na roli wiersz opisuje TĘ rolę, w której
              // administrator już stoi.
              onGoToSubject={
                subjectKind === 'user'
                  ? (kind, id) => {
                      if (kind === 'user') return;
                      setSubjectKind(kind);
                      setSubjectId(id);
                      setExplained(null);
                    }
                  : undefined
              }
            />
          );
        })()}
    </div>
  );
}
